import useAccessControl from '../../../../legacy/components/AccessControl/useAccessControl'
import { FeatureToggle } from '../../../../legacy/contexts/featureToggles/FeatureToggleContext'
import { ICPlatformVersion } from '../../../../utils/ic-platform/versioning/versions.constants'

/**
 * Feature toggle name for the tool satisfaction survey.
 * This roulette controls whether users see the survey after completing workflows.
 */
export const TOOL_SURVEY_FEATURE_TOGGLE = FeatureToggle.ToolSatisfactionSurvey

/**
 * Minimum IC Platform release version required for the tool satisfaction survey.
 * The survey will only be shown to users on this release version or later.
 */
export const TOOL_SURVEY_MIN_RELEASE_VERSION = ICPlatformVersion.r2026_01_0

/**
 * Hook to check if the tool satisfaction survey feature is enabled.
 *
 * This hook checks:
 * 1. IC Platform release version (must be R1 2026 or later)
 * 2. User-level and warehouse-level feature toggles
 *
 * Both the release gate AND the feature toggle must be satisfied for the survey to show.
 *
 * Note: The `ToolSurvey` component calls this hook internally, so you typically
 * don't need to use this hook directly. Just render `<ToolSurvey />` and it will
 * handle the roulette check automatically.
 *
 * @returns {boolean} Whether the tool satisfaction survey is enabled for the current user/warehouse
 *
 * @example
 * ```tsx
 * // The ToolSurvey component handles the roulette check internally,
 * // so you can just render it directly:
 * <ToolSurvey
 *   workflowSlug="my-workflow"
 *   isTriggered={workflowCompleted}
 *   onClose={handleClose}
 * />
 *
 * // If you need to check access separately (e.g., for conditional logic):
 * import { useToolSurveyAccess } from '@retailer-platform/dashboard/gin-and-tonic'
 *
 * const isToolSurveyEnabled = useToolSurveyAccess()
 * ```
 */
export const useToolSurveyAccess = (): boolean => {
  const hasAccess = useAccessControl()

  // Check release version gate (R1 2026)
  const hasReleaseAccess = hasAccess({
    minICPlatformVersion: TOOL_SURVEY_MIN_RELEASE_VERSION,
  })

  // Check user-level feature toggle
  const hasUserAccess = hasAccess({
    featureToggles: TOOL_SURVEY_FEATURE_TOGGLE,
  })

  // Check warehouse-level feature toggle
  const hasWarehouseAccess = hasAccess({
    warehouseFeatureToggles: [TOOL_SURVEY_FEATURE_TOGGLE],
  })

  // Both release version AND feature toggle must be satisfied
  return hasReleaseAccess && (hasUserAccess || hasWarehouseAccess)
}
